import * as XLSX from "xlsx";

import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from "chart.js";
import {
    getAdminBUData,
    getAdminLE,
    getAdminRegions,
    getAdminSectorData,
    getAdminSubBUData,
    getBULevelDashboardData,
    getCountryLevelDashboardData,
    getHitachiLevelDashboardData,
    getLELevelDashboardData,
    getNonAdminFiltersData,
    getRCMRegionFiltersData,
    getRegionLevelDashboardData,
    getSectorLevelDashboardData,
    getSubBULevelDashboardData,
} from "../../services/dashboard/dashboard-services";
import { useEffect, useState } from "react";

import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Dropdown from "./Dropdown";
import Loader from "./Loader/Loader";
import Multiselect from "multiselect-react-dropdown";
import { getRoles } from "../../services/dashboard/dashboard-services";
import useGetFiltersData from "../../utils/useGetFiltersData";
import { useTranslation } from "react-i18next";
import {dashboardKeyMap , filterKeyMap, labelKeyMap} from '../../utils/constants';
import i18next from "i18next";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

const StackedBarGraph = (props) => {
    const [slotsAndTemplatesData, isAdmin, loading] = useGetFiltersData();
    const defaultOptions = {
        indexAxis: "y",
        plugins: {
            datalabels: {
                display: true,
                color: function (context) {
                    if (context.dataset.label === "Aggregate") {
                        return "white";
                    }
                    return "black";
                },
                align: "center",
                formatter: (value) => {
                    if (value === 0) {
                        return "";
                    }
                    return `${value}%`;
                },
            },
        },
        scales: {
            x: {
                display: false,
            },
        },
    };

    const options = {
        ...defaultOptions,
        ...props?.options,
        plugins: {
            ...defaultOptions.plugins,
            ...props?.options?.plugins,
        },
        scales: {
            ...defaultOptions.scales,
            ...props?.options?.scales,
        },
    };

    const [nonAdminFiltersData, setNonAdminFiltersData] = useState([]);
    const [adminSectorData, setAdminSectorData] = useState([]);
    const [adminRegionData, setAdminRegionData] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [regionOptions, setRegionOptions] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countryOptions, setCountryOptions] = useState([]);
    const { t, i18n } = useTranslation();
    const [selectedSector, setSelectedSector] = useState(null);
    const [templateOptions, setTemplateOptions] = useState([]);
    const [sectorOptions, setSectorOptions] = useState([]);
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
    const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
    const [selectedSubBU, setSelectedSubBU] = useState(null);
    const [subBUOptions, setSubBUOptions] = useState([]);
    const [selectedLegalEntity, setSelectedLegalEntity] = useState(null);
    const [legalEntityOptions, setLegalEntityOptions] = useState([]);
    const [showDashboard, setShowDashboard] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPrimaryFilterSelected, setIsPrimaryFilterSelected] =
        useState(false);
    const [isSecondaryFilterSelected, setIsSecondaryFilterSelected] =
        useState(false);

    const [roleName, setRoleName] = useState("");
    const languageId = JSON.parse(sessionStorage.getItem("selectedLanguageDetails"))?.id || "1";

    useEffect(() => {
        setIsSecondaryFilterSelected(false);
        setIsPrimaryFilterSelected(false);
    }, [props.selectedDashboard]);

    useEffect(() => {
        // Helper to check if any primary filter is selected
        const isAnyPrimarySelected = () => {
            const filterMap = {
                Slot: selectedSlot,
                Template: selectedTemplate,
                Region: selectedRegion,
                Country: selectedCountry,
                Sector: selectedSector,
                "Business Unit": selectedBusinessUnit,
                "Sub BU": selectedSubBU,
                Entity: selectedLegalEntity,
            };
            return props?.primaryFilters?.some(
                (filter) => filterMap[filter.name]
            );
        };

        setIsPrimaryFilterSelected(isAnyPrimarySelected());
        props?.secondaryFilters &&
            props?.secondaryFilters.forEach((filter) => {
                if (filter.name === "Region") {
                    selectedRegion && setIsSecondaryFilterSelected(true);
                    return;
                } else if (filter.name === "Country") {
                    selectedCountry && setIsSecondaryFilterSelected(true);
                    return;
                } else if (filter.name === "Sector") {
                    selectedSector && setIsSecondaryFilterSelected(true);
                    return;
                } else if (filter.name === "Business Unit") {
                    selectedBusinessUnit && setIsSecondaryFilterSelected(true);
                    return;
                } else if (filter.name === "Sub BU") {
                    selectedSubBU && setIsSecondaryFilterSelected(true);
                    return;
                } else if (filter.name === "Entity") {
                    selectedLegalEntity && setIsSecondaryFilterSelected(true);
                    return;
                } else {
                    setIsSecondaryFilterSelected(false);
                }
            });
    }, [
        selectedSlot,
        selectedTemplate,
        selectedRegion,
        selectedCountry,
        selectedSector,
        selectedBusinessUnit,
        selectedSubBU,
        selectedLegalEntity,
    ]);

    useEffect(() => {
        if (isAdmin) {
            fetchAdminSectorData();
            fetchAdminRegions();
        } else {
            fetchRoles();
            // if (roleName === 'RCM-REGION') {
            //     fetchRCMRegionFiltersData();
            // } else {
            //     fetchNonAdminFiltersData();
            // }
        }
    }, [isAdmin, roleName]);

    useEffect(() => {
        setShowDashboard(false);
        setSelectedSlot(null);
        setSelectedTemplate(null);
        setSelectedRegion(null);
        setSelectedCountry(null);
        setSelectedSector(null);
        setSelectedBusinessUnit(null);
        setSelectedSubBU(null);
        setSelectedLegalEntity(null);
        setBusinessUnitOptions([]);
        setSubBUOptions([]);
        setLegalEntityOptions([]);
    }, [props?.data]);

    const handleResetPrimaryFilters = () => {
        props?.primaryFilters.forEach((filter) => {
            if (filter.name === "Slot") {
                setSelectedSlot(null);
            }
            if (filter.name === "Template") {
                setSelectedTemplate(null);
            }
            if (filter.name === "Region") {
                setSelectedRegion(null);
            }
            if (filter.name === "Country") {
                setSelectedCountry(null);
            }
            if (filter.name === "Sector") {
                setSelectedSector(null);
            }
            if (filter.name === "Business Unit") {
                setSelectedBusinessUnit(null);
            }
            if (filter.name === "Sub BU") {
                setSelectedSubBU(null);
            }
            if (filter.name === "Entity") {
                setSelectedLegalEntity(null);
            }
        });
        setIsPrimaryFilterSelected(false);
    };

    const handleResetSecondaryFilters = () => {
        props?.secondaryFilters.forEach((filter) => {
            if (filter.name === "Region") {
                setSelectedRegion(null);
            }
            if (filter.name === "Country") {
                setSelectedCountry(null);
            }
            if (filter.name === "Sector") {
                setSelectedSector(null);
            }
            if (filter.name === "Business Unit") {
                setSelectedBusinessUnit(null);
            }
            if (filter.name === "Sub BU") {
                setSelectedSubBU(null);
            }
            if (filter.name === "Entity") {
                setSelectedLegalEntity(null);
            }
        });
        setIsSecondaryFilterSelected(false);
    };

    const fetchRoles = async () => {
        try {
            const response = await getRoles();
            const roles = response.data.find(
                (role) => role.name === "RCM-REGION" || role.name === "RCM-COUNTRY"
            );
            setRoleName(roles?.name);
            if (roles?.name === "RCM-REGION" || roles?.name === "RCM-COUNTRY") {
                fetchRCMRegionFiltersData();
            } else {
                fetchNonAdminFiltersData();
            }
        } catch (error) {
            console.error("Error fetching role:", error);
        }
    };

    const fetchNonAdminFiltersData = async () => {
        try {
            setIsLoading(true);
            const response = await getNonAdminFiltersData();
            setNonAdminFiltersData(response.data);
        } catch (error) {
            console.error("Error fetching non-admin filters data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchRCMRegionFiltersData = async () => {
        try {
            setIsLoading(true);
            const response = await getRCMRegionFiltersData();
            setNonAdminFiltersData(response.data);
        } catch (error) {
            console.error("Error fetching non-admin filters data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAdminSectorData = async () => {
        try {
            setIsLoading(true);
            const response = await getAdminSectorData();
            setAdminSectorData(response.data);
        } catch (error) {
            console.error("Error fetching admin sector data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAdminBUData = async () => {
        try {
            setIsLoading(true);
            const response = await getAdminBUData(selectedSector);
            return response.data;
        } catch (error) {
            console.error("Error fetching admin sector data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAdminSubBUData = async () => {
        try {
            setIsLoading(true);
            const response = await getAdminSubBUData(selectedBusinessUnit);
            return response.data;
        } catch (error) {
            console.error("Error fetching admin sector data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAdminLegalEntitiesData = async () => {
        try {
            setIsLoading(true);
            const response = await getAdminLE([parseInt(selectedSubBU)]);
            return response.data;
        } catch (error) {
            console.error("Error fetching admin sector data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAdminRegions = async () => {
        try {
            setIsLoading(true);
            const response = await getAdminRegions();
            setAdminRegionData(response.data);
        } catch (error) {
            console.error("Error fetching admin sector data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin) {
            setSectorOptions(
                adminSectorData?.map((sector) => ({
                    value: sector.id,
                    label: sector.name,
                }))
            );
            setRegionOptions(
                adminRegionData?.map((region) => ({
                    value: region.regionId,
                    label: region.regionName,
                }))
            );
        } else {
            setSectorOptions(
                nonAdminFiltersData
                    ?.filter((item) => item.tierId === 2)
                    .map((item) => ({
                        value: item.id,
                        label: item.name,
                    }))
            );
            setRegionOptions(
                nonAdminFiltersData
                    ?.filter((item) => item.tierId === 5)
                    .map((item) => ({
                        value: item.regionId,
                        label: item.regionName,
                    }))
            );
        }
    }, [isAdmin, adminSectorData, nonAdminFiltersData]);

    useEffect(() => {
        if (slotsAndTemplatesData && selectedSlot) {
            setTemplateOptions(
                slotsAndTemplatesData
                    ?.filter((item) => item.slotId === parseInt(selectedSlot) && item.languageId === languageId)
                    .map((item) => ({
                        value: item.templateMasterId,
                        label: item.templateMasterName,
                    }))
            );
        }
    }, [slotsAndTemplatesData, selectedSlot, i18n.language]);

    useEffect(() => {
        const fetchData = async () => {
            if (selectedSector) {
                setSelectedBusinessUnit(null);
                setSelectedSubBU(null);
                setSelectedLegalEntity(null);
                if (props.selectedDashboard !== "Sector Dashboard") {
                    if (isAdmin) {
                        const adminBUData = await fetchAdminBUData();
                        adminBUData &&
                            setBusinessUnitOptions(
                                adminBUData.map((bu) => ({
                                    value: bu.id,
                                    label: bu.name,
                                }))
                            );
                    } else {
                        setBusinessUnitOptions(
                            nonAdminFiltersData
                                ?.filter(
                                    (item) =>
                                        item.tierId === 3 &&
                                        item.parentOrgId ===
                                        parseInt(selectedSector)
                                )
                                .map((item) => ({
                                    value: item.id,
                                    label: item.name,
                                }))
                        );
                    }
                }
            }
        };
        fetchData();
    }, [selectedSector]);

    useEffect(() => {
        const fetchData = async () => {
            if (selectedBusinessUnit) {
                setSelectedSubBU(null);
                setSelectedLegalEntity(null);
                if (props.selectedDashboard !== "Business Unit Dashboard") {
                    if (isAdmin) {
                        const adminSubBUData = await fetchAdminSubBUData();
                        adminSubBUData &&
                            setSubBUOptions(
                                adminSubBUData.map((subBU) => ({
                                    value: subBU.id,
                                    label: subBU.name,
                                }))
                            );
                    } else {
                        setSubBUOptions(
                            nonAdminFiltersData
                                ?.filter(
                                    (item) =>
                                        item.tierId === 4 &&
                                        item.parentOrgId ===
                                        parseInt(selectedBusinessUnit)
                                )
                                .map((item) => ({
                                    value: item.id,
                                    label: item.name,
                                }))
                        );
                    }
                }
            }
        };
        fetchData();
    }, [selectedBusinessUnit]);

    useEffect(() => {
        const fetchData = async () => {
            if (selectedSubBU) {
                setSelectedLegalEntity(null);
                if (props.selectedDashboard !== "Sub Business Unit Dashboard") {
                    if (isAdmin) {
                        const adminLEData = await fetchAdminLegalEntitiesData();
                        adminLEData &&
                            setLegalEntityOptions(
                                adminLEData.map((LE) => ({
                                    value: LE.id,
                                    label: LE.name,
                                }))
                            );
                    } else {
                        setLegalEntityOptions(
                            nonAdminFiltersData
                                ?.filter(
                                    (item) =>
                                        item.tierId === 5 &&
                                        item.parentOrgId ===
                                        parseInt(selectedSubBU)
                                )
                                .map((item) => ({
                                    value: item.id,
                                    label: item.name,
                                }))
                        );
                    }
                }
            }
        };
        fetchData();
    }, [selectedSubBU]);

    useEffect(() => {
        const fetchData = async () => {
            if (selectedRegion) {
                if (isAdmin) {
                    const regionIds = Array.isArray(selectedRegion)
                        ? selectedRegion.map((item) => item.value)
                        : [parseInt(selectedRegion)];
                    const countries = adminRegionData
                        .filter((region) => regionIds.includes(region.regionId))
                        .flatMap((region) => region.countries)
                        .map((country) => ({
                            value: country.countryId,
                            label: country.countryName,
                        }));
                    setCountryOptions(countries);
                    setSelectedCountry(null);
                } else {
                    Array.isArray(selectedRegion)
                        ? setCountryOptions(
                            nonAdminFiltersData
                                ?.filter(
                                    (item) =>
                                        item.tierId === 5 &&
                                        selectedRegion
                                            .map((item) => item.value)
                                            .includes(item.regionId)
                                )
                                .map((item) => ({
                                    value: item.countryId,
                                    label: item.countryName,
                                }))
                        )
                        : setCountryOptions(
                            nonAdminFiltersData
                                ?.filter(
                                    (item) =>
                                        item.tierId === 5 &&
                                        parseInt(selectedRegion) ===
                                        item.regionId
                                )
                                .map((item) => ({
                                    value: item.countryId,
                                    label: item.countryName,
                                }))
                        );
                }
            }
        };
        fetchData();
    }, [selectedRegion]);


    const renderFilters = (label) => {
        const filters = label.includes(t("primaryFilters"))
            ? props.primaryFilters
            : label.includes(t("secondaryFilters"))
                ? props.secondaryFilters
                : [];
        const addAllOption = (options) => {
            if (!options || options.length === 0) return [];
            if (options.some((opt) => opt.value === "__all__")) return options;
            const uniqueOptions = Array.from(
                new Map(options.map((opt) => [opt.value, opt])).values()
            );
            return [{ value: "__all__", label: "All" }, ...uniqueOptions];
        };

        const handleMultiSelectChange = (selected, setSelected, options) => {
            if (selected.some((item) => item.value === "__all__")) {
                setSelected(options.filter((opt) => opt.value !== "__all__"));
            } else {
                setSelected(selected);
            }
        };

        const handleMultiSelectRemove = (selected, setSelected) => {
            setSelected(selected);
        };

         const getSelected = (selected, options) => {
            return Array.isArray(selected) &&
                options.length > 0 &&
                selected.length === options.length &&
                selected.every((sel) =>
                    options.some((opt) => opt.value === sel.value)
                )
                ? [
                      {
                          value: "__all__",
                          label: "All",
                      },
                  ]
                : selected || [];
        };

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    marginBottom: "10px",
                }}
            >
                <div>
                    <h3
                        style={{
                            fontSize: "16px",
                            margin: 0,
                            fontWeight: "600",
                        }}
                    >
                        {label}
                    </h3>
                </div>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "row",
                        gap: "20px",
                        fontSize: "14px",
                    }}
                >
                    {filters?.map((filter) => (
                        <div
                            key={filter.name}
                            className={
                                filter.name !== "Sector"
                                    ? "dropdown-container"
                                    : "dropdown-container max-width-container"
                            }
                        >
                            <label>
                                {t(filterKeyMap[filter.name] || filter.name)}
                            </label>
                            {filter.name === "Slot" && (
                                <Dropdown
                                    options={Array.from(
                                        new Map(
                                            slotsAndTemplatesData?.map(
                                                (slot) => [
                                                    slot.slotId,
                                                    {
                                                        value: slot.slotId,
                                                        label: slot.slotName,
                                                    },
                                                ]
                                            )
                                        ).values()
                                    )}
                                    placeholder={t("selectSlot")}
                                    onDropdownChange={setSelectedSlot}
                                    value={selectedSlot || []}
                                />
                            )}
                            {filter.name === "Template" && (
                                <Dropdown
                                    options={templateOptions}
                                    disabled={!selectedSlot}
                                    placeholder={t("selectTemplate")}
                                    onDropdownChange={setSelectedTemplate}
                                    value={selectedTemplate || []}
                                />
                            )}

                            {filter.name === "Region" &&
                                (filter.isMultiSelect ? (
                                    <Multiselect
                                        options={addAllOption(regionOptions)}
                                        selectedValues={getSelected(
                                            selectedRegion,
                                            regionOptions
                                        )}
                                        onSelect={(selected) =>
                                            handleMultiSelectChange(
                                                selected,
                                                setSelectedRegion,
                                                addAllOption(regionOptions)
                                            )
                                        }
                                        onRemove={(selected) =>
                                            handleMultiSelectRemove(
                                                selected,
                                                setSelectedRegion
                                            )
                                        }
                                        displayValue="label"
                                        placeholder={t("selectRegion")}
                                        showCheckbox={true}
                                        style={{
                                            multiselectContainer: {
                                                width: "220px",
                                                color: "#c80000",
                                            },
                                            chips: {
                                                background: "#B80211",
                                                display: "flex",
                                            },
                                            option: {
                                                fontFamily: "sans-serif",
                                                fontSize: "12px",
                                                color: "black",
                                            },
                                        }}
                                    />
                                ) : (
                                    <Dropdown
                                        options={Array.from(
                                            new Map(
                                                regionOptions?.map((region) => [
                                                    region.value,
                                                    region,
                                                ])
                                            ).values()
                                        )}
                                        placeholder={t("selectRegion")}
                                        onDropdownChange={setSelectedRegion}
                                        value={selectedRegion || []}
                                    />
                                ))}
                            {/* Country */}
                            {filter.name === "Country" &&
                                (filter.isMultiSelect ? (
                                    <Multiselect
                                        options={addAllOption(countryOptions)}
                                        selectedValues={getSelected(
                                            selectedCountry,
                                            countryOptions
                                        )}
                                        onSelect={(selected) =>
                                            handleMultiSelectChange(
                                                selected,
                                                setSelectedCountry,
                                                addAllOption(countryOptions)
                                            )
                                        }
                                        onRemove={(selected) =>
                                            handleMultiSelectRemove(
                                                selected,
                                                setSelectedCountry
                                            )
                                        }
                                        displayValue="label"
                                        placeholder={t("selectCountry")}
                                        showCheckbox={true}
                                        style={{
                                            multiselectContainer: {
                                                width: "220px",
                                                color: "#c80000",
                                            },
                                            chips: {
                                                background: "#B80211",
                                                display: "flex",
                                            },
                                            option: {
                                                fontFamily: "sans-serif",
                                                fontSize: "12px",
                                                color: "black",
                                            },
                                        }}
                                        disable={!selectedRegion}
                                    />
                                ) : (
                                    <Dropdown
                                        options={Array.from(
                                            new Map(
                                                countryOptions?.map(
                                                    (country) => [
                                                        country.value,
                                                        country,
                                                    ]
                                                )
                                            ).values()
                                        )}
                                        placeholder={t("selectCountry")}
                                        isDisabled={!selectedRegion}
                                        onDropdownChange={setSelectedCountry}
                                        value={selectedCountry || []}
                                    />
                                ))}
                            {/* Sector */}
                            {filter.name === "Sector" &&
                                (filter.isMultiSelect ? (
                                    <Multiselect
                                        options={addAllOption(sectorOptions)}
                                        selectedValues={getSelected(
                                            selectedSector,
                                            sectorOptions
                                        )}
                                        onSelect={(selected) =>
                                            handleMultiSelectChange(
                                                selected,
                                                setSelectedSector,
                                                addAllOption(sectorOptions)
                                            )
                                        }
                                        onRemove={(selected) =>
                                            handleMultiSelectRemove(
                                                selected,
                                                setSelectedSector
                                            )
                                        }
                                        displayValue="label"
                                        placeholder={t("selectSector")}
                                        showCheckbox={true}
                                        style={{
                                            multiselectContainer: {
                                                color: "#c80000",
                                            },
                                            chips: {
                                                background: "#B80211",
                                                display: "flex",
                                            },
                                            option: {
                                                fontFamily: "sans-serif",
                                                fontSize: "12px",
                                                color: "black",
                                            },
                                        }}
                                    />
                                ) : (
                                    <Dropdown
                                        options={Array.from(
                                            new Map(
                                                sectorOptions?.map((sector) => [
                                                    sector.value,
                                                    sector,
                                                ])
                                            ).values()
                                        )}
                                        placeholder={t("selectSector")}
                                        onDropdownChange={setSelectedSector}
                                        value={selectedSector || []}
                                    />
                                ))}
                            {/* Business Unit */}
                            {filter.name === "Business Unit" &&
                                (filter.isMultiSelect ? (
                                    <Multiselect
                                        options={addAllOption(
                                            businessUnitOptions
                                        )}
                                        selectedValues={getSelected(
                                            selectedBusinessUnit,
                                            businessUnitOptions
                                        )}
                                        onSelect={(selected) =>
                                            handleMultiSelectChange(
                                                selected,
                                                setSelectedBusinessUnit,
                                                addAllOption(
                                                    businessUnitOptions
                                                )
                                            )
                                        }
                                        onRemove={(selected) =>
                                            handleMultiSelectRemove(
                                                selected,
                                                setSelectedBusinessUnit
                                            )
                                        }
                                        displayValue="label"
                                        placeholder={t("selectBusinessUnit")}
                                        showCheckbox={true}
                                        style={{
                                            multiselectContainer: {
                                                width: "220px",
                                                color: "#c80000",
                                            },
                                            chips: {
                                                background: "#B80211",
                                                display: "flex",
                                            },
                                            option: {
                                                fontFamily: "sans-serif",
                                                fontSize: "12px",
                                                color: "black",
                                            },
                                        }}
                                        disable={!selectedSector}
                                    />
                                ) : (
                                    <Dropdown
                                        options={Array.from(
                                            new Map(
                                                businessUnitOptions?.map(
                                                    (bu) => [bu.value, bu]
                                                )
                                            ).values()
                                        )}
                                        placeholder={t("selectBusinessUnit")}
                                        isDisabled={!selectedSector}
                                        onDropdownChange={
                                            setSelectedBusinessUnit
                                        }
                                        value={selectedBusinessUnit || []}
                                    />
                                ))}
                            {/* Sub BU */}
                            {filter.name === "Sub BU" &&
                                (filter.isMultiSelect ? (
                                    <Multiselect
                                        options={addAllOption(subBUOptions)}
                                        selectedValues={getSelected(
                                            selectedSubBU,
                                            subBUOptions
                                        )}
                                        onSelect={(selected) =>
                                            handleMultiSelectChange(
                                                selected,
                                                setSelectedSubBU,
                                                addAllOption(subBUOptions)
                                            )
                                        }
                                        onRemove={(selected) =>
                                            handleMultiSelectRemove(
                                                selected,
                                                setSelectedSubBU
                                            )
                                        }
                                        displayValue="label"
                                        placeholder={t("selectSubBU")}
                                        showCheckbox={true}
                                        style={{
                                            multiselectContainer: {
                                                width: "220px",
                                                color: "#c80000",
                                            },
                                            chips: {
                                                background: "#B80211",
                                                display: "flex",
                                            },
                                            option: {
                                                fontFamily: "sans-serif",
                                                fontSize: "12px",
                                                color: "black",
                                            },
                                        }}
                                        disable={!selectedBusinessUnit}
                                    />
                                ) : (
                                    <Dropdown
                                        options={Array.from(
                                            new Map(
                                                subBUOptions?.map((subBU) => [
                                                    subBU.value,
                                                    subBU,
                                                ])
                                            ).values()
                                        )}
                                        placeholder={t("selectSubBU")}
                                        isDisabled={!selectedBusinessUnit}
                                        onDropdownChange={setSelectedSubBU}
                                        value={selectedSubBU || []}
                                    />
                                ))}
                            {/* Entity */}
                            {filter.name === "Entity" &&
                                (filter.isMultiSelect ? (
                                    <Multiselect
                                        options={addAllOption(
                                            legalEntityOptions
                                        )}
                                        selectedValues={getSelected(
                                            selectedLegalEntity,
                                            legalEntityOptions
                                        )}
                                        onSelect={(selected) =>
                                            handleMultiSelectChange(
                                                selected,
                                                setSelectedLegalEntity,
                                                addAllOption(legalEntityOptions)
                                            )
                                        }
                                        onRemove={(selected) =>
                                            handleMultiSelectRemove(
                                                selected,
                                                setSelectedLegalEntity
                                            )
                                        }
                                        displayValue="label"
                                        placeholder={t("selectEntity")}
                                        showCheckbox={true}
                                        style={{
                                            multiselectContainer: {
                                                width: "220px",
                                                color: "#c80000",
                                            },
                                            chips: {
                                                background: "#B80211",
                                                display: "flex",
                                            },
                                            option: {
                                                fontFamily: "sans-serif",
                                                fontSize: "12px",
                                                color: "black",
                                            },
                                        }}
                                        disable={!selectedSubBU}
                                    />
                                ) : (
                                    <Dropdown
                                        options={Array.from(
                                            new Map(
                                                legalEntityOptions?.map(
                                                    (LE) => [LE.value, LE]
                                                )
                                            ).values()
                                        )}
                                        placeholder={t("selectEntity")}
                                        isDisabled={!selectedSubBU}
                                        onDropdownChange={
                                            setSelectedLegalEntity
                                        }
                                        value={selectedLegalEntity || []}
                                    />
                                ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const fetchHitachiDashboardData = async () => {
        const slotId = parseInt(selectedSlot);
        const templateMasterId = parseInt(selectedTemplate);
        try {
            const response = await getHitachiLevelDashboardData(
                slotId,
                templateMasterId
            );
            const updatedArray = response?.data?.map((item) => ({
                ...item,
                organizationName: "Hitachi Overall",
            }));
            return updatedArray;
        } catch (error) {
            return null;
        }
    };

    const fetchSectorDashboardData = async () => {
        const slotId = parseInt(selectedSlot);
        const templateMasterId = parseInt(selectedTemplate);
        let reqSector = [];
        if (selectedSector) {
            if (Array.isArray(selectedSector)) {
                reqSector = selectedSector.map((item) => item.value);
            } else {
                reqSector = [parseInt(selectedSector)];
            }
        }
        const reqData = {
            ids: reqSector,
            regionIds: selectedRegion ? [parseInt(selectedRegion)] : [],
            countryIds: selectedCountry ? [parseInt(selectedCountry)] : [],
        };
        try {
            const response = await getSectorLevelDashboardData(
                slotId,
                templateMasterId,
                reqData
            );
            return response.data;
        } catch (error) {
            return null;
        }
    };

    const fetchBUDashboardData = async () => {
        const slotId = parseInt(selectedSlot);
        const templateMasterId = parseInt(selectedTemplate);
        let reqBU = [];
        if (selectedBusinessUnit) {
            if (Array.isArray(selectedBusinessUnit)) {
                reqBU = selectedBusinessUnit.map((item) => item.value);
            } else {
                reqBU = [parseInt(selectedBusinessUnit)];
            }
        }
        const reqData = {
            ids: reqBU,
            regionIds: selectedRegion ? [parseInt(selectedRegion)] : [],
            countryIds: selectedCountry ? [parseInt(selectedCountry)] : [],
        };
        try {
            const response = await getBULevelDashboardData(
                slotId,
                templateMasterId,
                reqData
            );
            return response.data;
        } catch (error) {
            return null;
        }
    };

    const fetchSubBUDashboardData = async () => {
        const slotId = parseInt(selectedSlot);
        const templateMasterId = parseInt(selectedTemplate);
        let reqSubBU = [];
        if (selectedSubBU) {
            if (Array.isArray(selectedSubBU)) {
                reqSubBU = selectedSubBU.map((item) => item.value);
            } else {
                reqSubBU = [parseInt(selectedSubBU)];
            }
        }
        const reqData = {
            ids: reqSubBU,
            regionIds: selectedRegion ? [parseInt(selectedRegion)] : [],
            countryIds: selectedCountry ? [parseInt(selectedCountry)] : [],
        };
        try {
            const response = await getSubBULevelDashboardData(
                slotId,
                templateMasterId,
                reqData
            );
            return response.data;
        } catch (error) {
            return null;
        }
    };

    const fetchLEDashboardData = async () => {
        const slotId = parseInt(selectedSlot);
        const templateMasterId = parseInt(selectedTemplate);
        let reqLegalEntity = [];
        if (selectedLegalEntity) {
            if (Array.isArray(selectedLegalEntity)) {
                reqLegalEntity = selectedLegalEntity.map((item) => item.value);
            } else {
                reqLegalEntity = [parseInt(selectedLegalEntity)];
            }
        }
        const reqData = {
            ids: reqLegalEntity,
            regionIds: selectedRegion ? [parseInt(selectedRegion)] : [],
            countryIds: selectedCountry ? [parseInt(selectedCountry)] : [],
        };
        try {
            const response = await getLELevelDashboardData(
                slotId,
                templateMasterId,
                reqData
            );
            return response.data;
        } catch (error) {
            return null;
        }
    };

    const fetchRegionDashboardData = async () => {
        const slotId = parseInt(selectedSlot);
        const templateMasterId = parseInt(selectedTemplate);
        const regionIds = selectedRegion
            ? selectedRegion.map((item) => item.value)
            : [];
        let reqData = {};
        if (selectedLegalEntity) {
            reqData = {
                regionIds: regionIds,
                sectorId: parseInt(selectedSector),
                businessUnitId: parseInt(selectedBusinessUnit),
                subBusinessUnitId: parseInt(selectedSubBU),
                legalEntityId: parseInt(selectedLegalEntity),
            };
        } else if (selectedSubBU) {
            reqData = {
                regionIds: regionIds,
                sectorId: parseInt(selectedSector),
                businessUnitId: parseInt(selectedBusinessUnit),
                subBusinessUnitId: parseInt(selectedSubBU),
            };
        } else if (selectedBusinessUnit) {
            reqData = {
                regionIds: regionIds,
                sectorId: parseInt(selectedSector),
                businessUnitId: parseInt(selectedBusinessUnit),
            };
        } else if (selectedSector) {
            reqData = {
                regionIds: regionIds,
                sectorId: parseInt(selectedSector),
            };
        } else {
            reqData = {
                regionIds: regionIds,
            };
        }
        try {
            const response = await getRegionLevelDashboardData(
                slotId,
                templateMasterId,
                reqData
            );
            return response.data;
        } catch (error) {
            return null;
        }
    };

    const fetchCountryDashboardData = async () => {
        const slotId = parseInt(selectedSlot);
        const templateMasterId = parseInt(selectedTemplate);
        const regionId = selectedRegion ? parseInt(selectedRegion) : 0;
        const countryIds = selectedCountry
            ? selectedCountry.map((item) => item.value)
            : [];
        let reqData = {};
        if (selectedLegalEntity) {
            reqData = {
                regionId: regionId,
                countryIds: countryIds,
                sectorId: parseInt(selectedSector),
                businessUnitId: parseInt(selectedBusinessUnit),
                subBusinessUnitId: parseInt(selectedSubBU),
                legalEntityId: parseInt(selectedLegalEntity),
            };
        } else if (selectedSubBU) {
            reqData = {
                regionIds: regionId,
                countryIds: countryIds,
                sectorId: parseInt(selectedSector),
                businessUnitId: parseInt(selectedBusinessUnit),
                subBusinessUnitId: parseInt(selectedSubBU),
            };
        } else if (selectedBusinessUnit) {
            reqData = {
                regionId: regionId,
                countryIds: countryIds,
                sectorId: parseInt(selectedSector),
                businessUnitId: parseInt(selectedBusinessUnit),
            };
        } else if (selectedSector) {
            reqData = {
                regionId: regionId,
                countryIds: countryIds,
                sectorId: parseInt(selectedSector),
            };
        } else {
            reqData = {
                regionId: regionId,
                countryIds: countryIds,
            };
        }
        try {
            const response = await getCountryLevelDashboardData(
                slotId,
                templateMasterId,
                reqData
            );
            return response.data;
        } catch (error) {
            return null;
        }
    };

    const handleGenerateDashboard = async () => {
        setIsLoading(true);
        let data = [];
        if (props?.selectedDashboard.trim() === "Hitachi Overall Dashboard") {
            data = await fetchHitachiDashboardData();

            if (!data) {
                console.error("Error fetching dashboard data");
                setShowDashboard(false);
                setDashboardData(null);
                setIsLoading(false);
                return;
            } else if (data.length === 0) {
                setShowDashboard(false);
                setDashboardData([]);
                setIsLoading(false);
                return;
            } else {
                const levelPercentages = data.map((item) => item.percentage);
                if (levelPercentages[1] === undefined) levelPercentages[1] = 0;
                if (levelPercentages[2] === undefined) levelPercentages[2] = 0;
                levelPercentages[3] = data[0].aggregatePercentage;
                setShowDashboard(true);
                const filteredDataSets = props.data.datasets.map(
                    (dataset, index) => {
                        return {
                            ...dataset,
                            data: [levelPercentages[index]],
                        };
                    }
                );
                const filteredData = {
                    ...props.data,
                    labels: [
                        ...new Set(data.map((item) => item.organizationName)),
                    ],
                    datasets: filteredDataSets,
                };
                setDashboardData(filteredData);
            }
        } else if (props?.selectedDashboard.trim() === "Sector Dashboard") {
            data = await fetchSectorDashboardData();

            if (!data) {
                console.error("Error fetching dashboard data");
                setShowDashboard(false);
                setDashboardData(null);
                setIsLoading(false);
                return;
            } else if (data.length === 0) {
                setShowDashboard(false);
                setDashboardData([]);
                setIsLoading(false);
                return;
            } else {
                const percentagesByLevel = Object.values(
                    data.reduce((acc, entry) => {
                        const { levelId, percentage } = entry;
                        if (!acc[levelId]) {
                            acc[levelId] = [];
                        }
                        acc[levelId].push(percentage);
                        return acc;
                    }, {})
                );

                const aggregatePercentagesByLevel = data
                    .filter((item) => item.levelId === 1)
                    .map((item) => item.aggregatePercentage);

                if (percentagesByLevel[1] === undefined)
                    percentagesByLevel[1] = [0];
                if (percentagesByLevel[2] === undefined)
                    percentagesByLevel[2] = [0];
                percentagesByLevel[3] = aggregatePercentagesByLevel;
                const filteredDataSets = props.data.datasets.map(
                    (dataset, index) => {
                        return {
                            ...dataset,
                            data: percentagesByLevel[index],
                        };
                    }
                );
                const filteredData = {
                    ...props.data,
                    labels: [
                        ...new Set(data.map((item) => item.organizationName)),
                    ],
                    datasets: filteredDataSets,
                };
                const hitachiData = await fetchHitachiDashboardData();
                if (hitachiData && hitachiData.length > 0) {
                    const levelPercentages = hitachiData.map(
                        (item) => item.percentage
                    );
                    if (levelPercentages[1] === undefined)
                        levelPercentages[1] = 0;
                    if (levelPercentages[2] === undefined)
                        levelPercentages[2] = 0;
                    levelPercentages[3] = hitachiData[0].aggregatePercentage;
                    const filteredhitachiDataSets = props.data.datasets.map(
                        (dataset, index) => {
                            return {
                                ...dataset,
                                data: [levelPercentages[index]],
                            };
                        }
                    );
                    const filteredHitachiData = {
                        ...props.data,
                        labels: [
                            ...new Set(
                                hitachiData.map((item) => item.organizationName)
                            ),
                        ],
                        datasets: filteredhitachiDataSets,
                    };
                    const appendedData = {
                        ...filteredHitachiData,
                        labels: [
                            ...filteredHitachiData.labels,
                            ...filteredData.labels,
                        ],
                        datasets: filteredHitachiData.datasets.map(
                            (dataset, index) => ({
                                ...dataset,
                                data: [
                                    ...dataset.data,
                                    ...filteredData.datasets[index].data,
                                ],
                            })
                        ),
                    };

                    setShowDashboard(true);
                    setDashboardData(appendedData);
                }
            }
        } else if (
            props?.selectedDashboard.trim() === "Business Unit Dashboard"
        ) {
            data = await fetchBUDashboardData();

            if (!data) {
                console.error("Error fetching dashboard data");
                setShowDashboard(false);
                setDashboardData(null);
                setIsLoading(false);
                return;
            } else if (data.length === 0) {
                setShowDashboard(false);
                setDashboardData([]);
                setIsLoading(false);
                return;
            } else {
                const percentagesByLevel = Object.values(
                    data.reduce((acc, entry) => {
                        const { levelId, percentage } = entry;
                        if (!acc[levelId]) {
                            acc[levelId] = [];
                        }
                        acc[levelId].push(percentage);
                        return acc;
                    }, {})
                );

                const aggregatePercentagesByLevel = data
                    .filter((item) => item.levelId === 1)
                    .map((item) => item.aggregatePercentage);

                if (percentagesByLevel[1] === undefined)
                    percentagesByLevel[1] = [0];
                if (percentagesByLevel[2] === undefined)
                    percentagesByLevel[2] = [0];
                percentagesByLevel[3] = aggregatePercentagesByLevel;
                const filteredDataSets = props.data.datasets.map(
                    (dataset, index) => {
                        return {
                            ...dataset,
                            data: percentagesByLevel[index],
                        };
                    }
                );
                const filteredData = {
                    ...props.data,
                    labels: [
                        ...new Set(data.map((item) => item.organizationName)),
                    ],
                    datasets: filteredDataSets,
                };
                const hitachiData = await fetchHitachiDashboardData();
                if (hitachiData && hitachiData.length > 0) {
                    const levelPercentages = hitachiData.map(
                        (item) => item.percentage
                    );
                    if (levelPercentages[1] === undefined)
                        levelPercentages[1] = 0;
                    if (levelPercentages[2] === undefined)
                        levelPercentages[2] = 0;
                    levelPercentages[3] = hitachiData[0].aggregatePercentage;
                    const filteredhitachiDataSets = props.data.datasets.map(
                        (dataset, index) => {
                            return {
                                ...dataset,
                                data: [levelPercentages[index]],
                            };
                        }
                    );
                    const filteredHitachiData = {
                        ...props.data,
                        labels: [
                            ...new Set(
                                hitachiData.map((item) => item.organizationName)
                            ),
                        ],
                        datasets: filteredhitachiDataSets,
                    };
                    const appendedData = {
                        ...filteredHitachiData,
                        labels: [
                            ...filteredHitachiData.labels,
                            ...filteredData.labels,
                        ],
                        datasets: filteredHitachiData.datasets.map(
                            (dataset, index) => ({
                                ...dataset,
                                data: [
                                    ...dataset.data,
                                    ...filteredData.datasets[index].data,
                                ],
                            })
                        ),
                    };

                    setShowDashboard(true);
                    setDashboardData(appendedData);
                }
            }
        } else if (
            props?.selectedDashboard.trim() === "Sub Business Unit Dashboard"
        ) {
            data = await fetchSubBUDashboardData();

            if (!data) {
                console.error("Error fetching dashboard data");
                setShowDashboard(false);
                setDashboardData(null);
                setIsLoading(false);
                return;
            } else if (data.length === 0) {
                setShowDashboard(false);
                setDashboardData([]);
                setIsLoading(false);
                return;
            } else {
                const percentagesByLevel = Object.values(
                    data.reduce((acc, entry) => {
                        const { levelId, percentage } = entry;
                        if (!acc[levelId]) {
                            acc[levelId] = [];
                        }
                        acc[levelId].push(percentage);
                        return acc;
                    }, {})
                );

                const aggregatePercentagesByLevel = data
                    .filter((item) => item.levelId === 1)
                    .map((item) => item.aggregatePercentage);

                if (percentagesByLevel[1] === undefined)
                    percentagesByLevel[1] = [0];
                if (percentagesByLevel[2] === undefined)
                    percentagesByLevel[2] = [0];
                percentagesByLevel[3] = aggregatePercentagesByLevel;
                const filteredDataSets = props.data.datasets.map(
                    (dataset, index) => {
                        return {
                            ...dataset,
                            data: percentagesByLevel[index],
                        };
                    }
                );
                const filteredData = {
                    ...props.data,
                    labels: [
                        ...new Set(data.map((item) => item.organizationName)),
                    ],
                    datasets: filteredDataSets,
                };
                const hitachiData = await fetchHitachiDashboardData();
                if (hitachiData && hitachiData.length > 0) {
                    const levelPercentages = hitachiData.map(
                        (item) => item.percentage
                    );
                    if (levelPercentages[1] === undefined)
                        levelPercentages[1] = 0;
                    if (levelPercentages[2] === undefined)
                        levelPercentages[2] = 0;
                    levelPercentages[3] = hitachiData[0].aggregatePercentage;
                    const filteredhitachiDataSets = props.data.datasets.map(
                        (dataset, index) => {
                            return {
                                ...dataset,
                                data: [levelPercentages[index]],
                            };
                        }
                    );
                    const filteredHitachiData = {
                        ...props.data,
                        labels: [
                            ...new Set(
                                hitachiData.map((item) => item.organizationName)
                            ),
                        ],
                        datasets: filteredhitachiDataSets,
                    };
                    const appendedData = {
                        ...filteredHitachiData,
                        labels: [
                            ...filteredHitachiData.labels,
                            ...filteredData.labels,
                        ],
                        datasets: filteredHitachiData.datasets.map(
                            (dataset, index) => ({
                                ...dataset,
                                data: [
                                    ...dataset.data,
                                    ...filteredData.datasets[index].data,
                                ],
                            })
                        ),
                    };

                    setShowDashboard(true);
                    setDashboardData(appendedData);
                }
            }
        } else if (props?.selectedDashboard.trim() === "Entity Dashboard") {
            data = await fetchLEDashboardData();

            if (!data) {
                console.error("Error fetching dashboard data");
                setShowDashboard(false);
                setDashboardData(null);
                setIsLoading(false);
                return;
            } else if (data.length === 0) {
                setShowDashboard(false);
                setDashboardData([]);
                setIsLoading(false);
                return;
            } else {
                const grouped = {};
                data.forEach((item) => {
                    if (!grouped[item.organizationId])
                        grouped[item.organizationId] = {};
                    grouped[item.organizationId][item.levelId] =
                        item.percentage;
                });

                const levelIds = [
                    ...new Set(data.map((item) => item.levelId)),
                ].sort();

                const orgIds = Object.keys(grouped).reverse();

                const percentagesByLevel = levelIds.map((levelId) =>
                    orgIds.map((orgId) => grouped[orgId][levelId] ?? 0)
                );

                const aggregatePercentagesByLevel = data
                    .filter((item) => item.levelId === 1)
                    .map((item) => item.aggregatePercentage);

                if (percentagesByLevel[1] === undefined)
                    percentagesByLevel[1] = [0];
                if (percentagesByLevel[2] === undefined)
                    percentagesByLevel[2] = [0];
                percentagesByLevel[3] = aggregatePercentagesByLevel;
                const filteredDataSets = props.data.datasets.map(
                    (dataset, index) => {
                        return {
                            ...dataset,
                            data: percentagesByLevel[index],
                        };
                    }
                );
                const filteredData = {
                    ...props.data,
                    labels: [
                        ...new Set(data.map((item) => item.organizationName)),
                    ],
                    datasets: filteredDataSets,
                };
                const hitachiData = await fetchHitachiDashboardData();
                if (hitachiData && hitachiData.length > 0) {
                    const levelPercentages = hitachiData.map(
                        (item) => item.percentage
                    );
                    if (levelPercentages[1] === undefined)
                        levelPercentages[1] = 0;
                    if (levelPercentages[2] === undefined)
                        levelPercentages[2] = 0;
                    levelPercentages[3] = hitachiData[0].aggregatePercentage;
                    const filteredhitachiDataSets = props.data.datasets.map(
                        (dataset, index) => {
                            return {
                                ...dataset,
                                data: [levelPercentages[index]],
                            };
                        }
                    );
                    const filteredHitachiData = {
                        ...props.data,
                        labels: [
                            ...new Set(
                                hitachiData.map((item) => item.organizationName)
                            ),
                        ],
                        datasets: filteredhitachiDataSets,
                    };
                    const appendedData = {
                        ...filteredHitachiData,
                        labels: [
                            ...filteredHitachiData.labels,
                            ...filteredData.labels,
                        ],
                        datasets: filteredHitachiData.datasets.map(
                            (dataset, index) => ({
                                ...dataset,
                                data: [
                                    ...dataset.data,
                                    ...filteredData.datasets[index].data,
                                ],
                            })
                        ),
                    };

                    setShowDashboard(true);
                    setDashboardData(appendedData);
                }
            }
        } else if (props?.selectedDashboard.trim() === "Region Dashboard") {
            data = await fetchRegionDashboardData();

            if (!data) {
                console.error("Error fetching dashboard data");
                setShowDashboard(false);
                setDashboardData(null);
                setIsLoading(false);
                return;
            } else if (data.length === 0) {
                setShowDashboard(false);
                setDashboardData([]);
                setIsLoading(false);
                return;
            } else {
                const percentagesByLevel = Object.values(
                    data.reduce((acc, entry) => {
                        const { levelId, percentage } = entry;
                        if (!acc[levelId]) {
                            acc[levelId] = [];
                        }
                        acc[levelId].push(percentage);
                        return acc;
                    }, {})
                );

                const aggregatePercentagesByLevel = data
                    .filter((item) => item.levelId === 1)
                    .map((item) => item.aggregatePercentage);

                if (percentagesByLevel[1] === undefined)
                    percentagesByLevel[1] = [0];
                if (percentagesByLevel[2] === undefined)
                    percentagesByLevel[2] = [0];
                percentagesByLevel[3] = aggregatePercentagesByLevel;
                const filteredDataSets = props.data.datasets.map(
                    (dataset, index) => {
                        return {
                            ...dataset,
                            data: percentagesByLevel[index],
                        };
                    }
                );
                const filteredData = {
                    ...props.data,
                    labels: [...new Set(data.map((item) => item.regionName))],
                    datasets: filteredDataSets,
                };
                const hitachiData = await fetchHitachiDashboardData();
                if (hitachiData && hitachiData.length > 0) {
                    const levelPercentages = hitachiData.map(
                        (item) => item.percentage
                    );
                    if (levelPercentages[1] === undefined)
                        levelPercentages[1] = 0;
                    if (levelPercentages[2] === undefined)
                        levelPercentages[2] = 0;
                    levelPercentages[3] = hitachiData[0].aggregatePercentage;
                    const filteredhitachiDataSets = props.data.datasets.map(
                        (dataset, index) => {
                            return {
                                ...dataset,
                                data: [levelPercentages[index]],
                            };
                        }
                    );
                    const filteredHitachiData = {
                        ...props.data,
                        labels: [
                            ...new Set(
                                hitachiData.map((item) => item.organizationName)
                            ),
                        ],
                        datasets: filteredhitachiDataSets,
                    };
                    const appendedData = {
                        ...filteredHitachiData,
                        labels: [
                            ...filteredHitachiData.labels,
                            ...filteredData.labels,
                        ],
                        datasets: filteredHitachiData.datasets.map(
                            (dataset, index) => ({
                                ...dataset,
                                data: [
                                    ...dataset.data,
                                    ...filteredData.datasets[index].data,
                                ],
                            })
                        ),
                    };

                    setShowDashboard(true);
                    setDashboardData(appendedData);
                }
            }
        } else if (props?.selectedDashboard.trim() === "Country Dashboard") {
            data = await fetchCountryDashboardData();

            if (!data) {
                console.error("Error fetching dashboard data");
                setShowDashboard(false);
                setDashboardData(null);
                setIsLoading(false);
                return;
            } else if (data.length === 0) {
                setShowDashboard(false);
                setDashboardData([]);
                setIsLoading(false);
                return;
            } else {
                const percentagesByLevel = Object.values(
                    data.reduce((acc, entry) => {
                        const { levelId, percentage } = entry;
                        if (!acc[levelId]) {
                            acc[levelId] = [];
                        }
                        acc[levelId].push(percentage);
                        return acc;
                    }, {})
                );

                const aggregatePercentagesByLevel = data
                    .filter((item) => item.levelId === 1)
                    .map((item) => item.aggregatePercentage);

                if (percentagesByLevel[1] === undefined)
                    percentagesByLevel[1] = [0];
                if (percentagesByLevel[2] === undefined)
                    percentagesByLevel[2] = [0];
                percentagesByLevel[3] = aggregatePercentagesByLevel;
                const filteredDataSets = props.data.datasets.map(
                    (dataset, index) => {
                        return {
                            ...dataset,
                            data: percentagesByLevel[index],
                        };
                    }
                );
                const filteredData = {
                    ...props.data,
                    labels: [...new Set(data.map((item) => item.countryName))],
                    datasets: filteredDataSets,
                };
                const hitachiData = await fetchHitachiDashboardData();
                if (hitachiData && hitachiData.length > 0) {
                    const levelPercentages = hitachiData.map(
                        (item) => item.percentage
                    );
                    if (levelPercentages[1] === undefined)
                        levelPercentages[1] = 0;
                    if (levelPercentages[2] === undefined)
                        levelPercentages[2] = 0;
                    levelPercentages[3] = hitachiData[0].aggregatePercentage;
                    const filteredhitachiDataSets = props.data.datasets.map(
                        (dataset, index) => {
                            return {
                                ...dataset,
                                data: [levelPercentages[index]],
                            };
                        }
                    );
                    const filteredHitachiData = {
                        ...props.data,
                        labels: [
                            ...new Set(
                                hitachiData.map((item) => item.organizationName)
                            ),
                        ],
                        datasets: filteredhitachiDataSets,
                    };
                    const appendedData = {
                        ...filteredHitachiData,
                        labels: [
                            ...filteredHitachiData.labels,
                            ...filteredData.labels,
                        ],
                        datasets: filteredHitachiData.datasets.map(
                            (dataset, index) => ({
                                ...dataset,
                                data: [
                                    ...dataset.data,
                                    ...filteredData.datasets[index].data,
                                ],
                            })
                        ),
                    };

                    setShowDashboard(true);
                    setDashboardData(appendedData);
                }
            }
        } else {
            setShowDashboard(true);
            setDashboardData(props?.data);
        }
        setIsLoading(false);
    };

    const handleExportToExcel = () => {
        if (!dashboardData || dashboardData.length === 0) {
            console.error("No data available to export.");
            return;
        }
        let dashboardRoundedData = dashboardData;
        console.log(dashboardData);

        if (dashboardData && dashboardData.datasets) {
            dashboardRoundedData = {
                ...dashboardData,
                datasets: dashboardData.datasets.map((dataset) => ({
                    ...dataset,
                    data: dataset.data.map((value) =>
                        typeof value === "number" ? Math.round(value) : value
                    ),
                })),
            };
        }

        const excelData = dashboardRoundedData.labels.map((label, index) => {
            const row = {};
            row[t("organizationName")] = t(label); // translate organization name if needed
            dashboardRoundedData.datasets.forEach((dataset) => {
                // Use the mapped key if available, otherwise fallback to dataset.label
                const translationKey = labelKeyMap[dataset.label] || dataset.label;
                row[t(translationKey)] = dataset.data[index];
            });
            return row;
        });
        let worksheet;
        const primaryFiltersList = props?.primaryFilters
            ? props.primaryFilters.map((item) => t(filterKeyMap[item.name] || item.name))
            : [];
            console.log(props?.primaryFilters);
        if (!props?.secondaryFilters) {
            worksheet = XLSX.utils.json_to_sheet(excelData, {
                origin: `A${props?.primaryFilters?.length + 5}`,
            });
        } else {
            worksheet = XLSX.utils.json_to_sheet(excelData, {
                origin: `A${props?.primaryFilters?.length + props?.secondaryFilters?.length + 7}`,
            });
            XLSX.utils.sheet_add_aoa(worksheet, [[t("secondaryFilters")]], {
                origin: `A${primaryFiltersList.length + 5}`,
            });
            // const secondaryFiltersList = props?.secondaryFilters
            //     ? props.secondaryFilters.map((item) => t(filterKeyMap[item.name] || item.name))
            //     : [];
            props?.secondaryFilters?.forEach((filter, idx) => {
            const filterLabel = t(filterKeyMap[filter.name] || filter.name);
            XLSX.utils.sheet_add_aoa(worksheet, [[filterLabel]], {
                origin: `A${primaryFiltersList.length + 6 + idx}`,
            });
            XLSX.utils.sheet_add_aoa(
                worksheet,
                [[getSelectedFilters(filter.name)]],
                {
                    origin: `B${primaryFiltersList.length + 6 + idx}`,
                }
            );
        });
        }
        XLSX.utils.sheet_add_aoa(
            worksheet,
            [[t(dashboardKeyMap[props.selectedDashboard] || props.selectedDashboard)]],
            { origin: "A1" }
        );
        XLSX.utils.sheet_add_aoa(worksheet, [[t("primaryFilters")]], {
            origin: "A3",
        });
        props?.primaryFilters?.forEach((filter, idx) => {
            const filterLabel = t(filterKeyMap[filter.name] || filter.name);
            XLSX.utils.sheet_add_aoa(worksheet, [[filterLabel]], {
                origin: `A${4 + idx}`,
            });
            XLSX.utils.sheet_add_aoa(
                worksheet,
                [[getSelectedFilters(filter.name)]], // <-- pass raw name here!
                {
                    origin: `B${4 + idx}`,
                }
            );
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, t("dashboardData"));

        XLSX.writeFile(workbook, "DashboardData.xlsx");
    };

    const getSelectedFilters = (filter) => {
        if (filter === "Region") {
            if (!selectedRegion) return "-";
            if (Array.isArray(selectedRegion)) {
                return selectedRegion
                    .map((item) => {
                        const value =
                            typeof item === "object" ? item.value : item;
                        const option = regionOptions.find(
                            (opt) => opt.value === value
                        );
                        return option ? t(option.label) : value;
                    })
                    .join(", ");
            } else {
                const option = regionOptions.find(
                    (opt) => opt.value === parseInt(selectedRegion)
                );
                return option ? t(option.label) : selectedRegion;
            }
        } else if (filter === "Country") {
            if (!selectedCountry) return "-";
            if (Array.isArray(selectedCountry)) {
                return selectedCountry
                    .map((item) => {
                        const value =
                            typeof item === "object" ? item.value : item;
                        const option = countryOptions.find(
                            (opt) => opt.value === value
                        );
                        return option ? option.label : value;
                    })
                    .join(", ");
            } else {
                const option = countryOptions.find(
                    (opt) => opt.value === parseInt(selectedCountry)
                );
                return option ? option.label : selectedCountry;
            }
        } else if (filter === "Sector") {
            if (!selectedSector) return "-";
            if (Array.isArray(selectedSector)) {
                return selectedSector
                    .map((item) => {
                        const value =
                            typeof item === "object" ? item.value : item;
                        const option = sectorOptions.find(
                            (opt) => opt.value === value
                        );
                        return option ? t(option.label) : value;
                    })
                    .join(", ");
            } else {
                const option = sectorOptions.find(
                    (opt) => opt.value === parseInt(selectedSector)
                );
                return option ? t(option.label) : selectedSector;
            }
        } else if (filter === "Business Unit") {
            if (!selectedBusinessUnit) return "-";
            if (Array.isArray(selectedBusinessUnit)) {
                return selectedBusinessUnit
                    .map((item) => {
                        const value =
                            typeof item === "object" ? item.value : item;
                        const option = businessUnitOptions.find(
                            (opt) => opt.value === value
                        );
                        return option ? t(option.label) : value;
                    })
                    .join(", ");
            } else {
                const option = businessUnitOptions.find(
                    (opt) => opt.value === parseInt(selectedBusinessUnit)
                );
                return option ? t(option.label) : selectedBusinessUnit;
            }
        } else if (filter === "Sub BU") {
            if (!selectedSubBU) return "-";
            if (Array.isArray(selectedSubBU)) {
                return selectedSubBU
                    .map((item) => {
                        const value =
                            typeof item === "object" ? item.value : item;
                        const option = subBUOptions.find(
                            (opt) => opt.value === value
                        );
                        return option ? t(option.label) : value;
                    })
                    .join(", ");
            } else {
                const option = subBUOptions.find(
                    (opt) => opt.value === parseInt(selectedSubBU)
                );
                return option ? t(option.label) : selectedSubBU;
            }
        } else if (filter === "Entity") {
            if (!selectedLegalEntity) return "-";
            if (Array.isArray(selectedLegalEntity)) {
                return selectedLegalEntity
                    .map((item) => {
                        const value =
                            typeof item === "object" ? item.value : item;
                        const option = legalEntityOptions.find(
                            (opt) => opt.value === value
                        );
                        return option ? t(option.label) : value;
                    })
                    .join(", ");
            } else {
                const option = legalEntityOptions.find(
                    (opt) => opt.value === parseInt(selectedLegalEntity)
                );
                return option ? t(option.label) : selectedLegalEntity;
            }
        } else if (filter === "Slot") {
            if (!selectedSlot) return "-";
            const option = slotsAndTemplatesData.find(
                (opt) => opt.slotId === parseInt(selectedSlot)
            );
            return option ? option.slotName : selectedSlot;
        } else if (filter === "Template") {
            if (!selectedTemplate) return "-";
            const options = slotsAndTemplatesData.filter(
                (opt) => opt.templateMasterId === parseInt(selectedTemplate)
            );
            if (!options || options.length === 0) return selectedTemplate;
            const languageId = JSON.parse(sessionStorage.getItem("selectedLanguageDetails"))?.id || 1;
            // Try to find the object with the correct languageId
            let option = options.find(opt => opt.languageId === languageId);
            if (!option) {
                // Fallback to default (usually English)
                option = options.find(opt => opt.languageId === 1) || options[0];
            }
            return option.templateMasterName;
        }
    };

    const handleResetFilters = () => {
        props?.secondaryFilters.forEach((filter) => {
            if (filter.name === "Region") {
                setSelectedRegion("");
            }
            if (filter.name === "Country") {
                setSelectedCountry("");
            }
            if (filter.name === "Sector") {
                setSelectedSector("");
            }
            if (filter.name === "Business Unit") {
                setSelectedBusinessUnit("");
            }
            if (filter.name === "Sub BU") {
                setSelectedSubBU("");
            }
            if (filter.name === "Entity") {
                setSelectedLegalEntity("");
            }
        });
    };

    const isGenerateDashboardDisabled = () => {
        if (!selectedSlot || !selectedTemplate) {
            return true;
        } else {
            if (props?.selectedDashboard.trim() === "Sector Dashboard") {
                if (!selectedSector) {
                    return true;
                }
            } else if (
                props?.selectedDashboard.trim() === "Business Unit Dashboard"
            ) {
                if (!selectedBusinessUnit) {
                    return true;
                }
            } else if (
                props?.selectedDashboard.trim() ===
                "Sub Business Unit Dashboard"
            ) {
                if (!selectedSubBU) {
                    return true;
                }
            } else if (props?.selectedDashboard.trim() === "Entity Dashboard") {
                if (!selectedLegalEntity) {
                    return true;
                }
            } else if (props?.selectedDashboard.trim() === "Region Dashboard") {
                if (!selectedRegion) {
                    return true;
                }
            } else if (
                props?.selectedDashboard.trim() === "Country Dashboard"
            ) {
                if (!selectedCountry) {
                    return true;
                }
            } else return false;
        }
    };

    if (loading || isLoading) return <Loader />;

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#f8f8f8",
                    padding: "15px",
                }}
            >
                {props.primaryFilters && renderFilters(t("primaryFilters"))}
                {props.secondaryFilters && renderFilters(t("secondaryFilters"))}
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "10px",
                    }}
                >
                    {isPrimaryFilterSelected && (
                        <button
                            className="resetButton"
                            onClick={handleResetPrimaryFilters}
                        >
                            {t("primaryFilters")}{" "}
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M8.5 0.625C4.15738 0.625 0.625 4.15825 0.625 8.5C0.625 12.8418 4.15738 16.375 8.5 16.375C12.8426 16.375 16.375 12.8418 16.375 8.5C16.375 4.15825 12.8426 0.625 8.5 0.625ZM8.5 14.625C5.12337 14.625 2.375 11.8775 2.375 8.5C2.375 5.1225 5.12337 2.375 8.5 2.375C11.8766 2.375 14.625 5.1225 14.625 8.5C14.625 11.8775 11.8766 14.625 8.5 14.625ZM9.11862 8.5L11.4339 6.18475C11.5159 6.10271 11.562 5.99145 11.562 5.87544C11.562 5.75943 11.5159 5.64817 11.4339 5.56613C11.3518 5.48411 11.2406 5.43803 11.1246 5.43803C11.0086 5.43803 10.8973 5.48411 10.8153 5.56613L8.5 7.88138L6.18475 5.56525C6.10224 5.48556 5.99172 5.44146 5.87701 5.44246C5.7623 5.44345 5.65257 5.48946 5.57145 5.57058C5.49034 5.6517 5.44433 5.76143 5.44333 5.87614C5.44233 5.99085 5.48643 6.10136 5.56613 6.18387L7.88138 8.5L5.56613 10.8153C5.48397 10.8973 5.43778 11.0086 5.43769 11.1247C5.43761 11.2408 5.48365 11.3522 5.56569 11.4343C5.64772 11.5165 5.75903 11.5627 5.87513 11.5627C5.99123 11.5628 6.1026 11.5168 6.18475 11.4347L8.5 9.11862L10.8153 11.4339C10.8555 11.4757 10.9038 11.5091 10.9571 11.5321C11.0105 11.5551 11.0679 11.5673 11.1259 11.5678C11.184 11.5684 11.2417 11.5574 11.2954 11.5355C11.3492 11.5136 11.3981 11.4812 11.4393 11.4402C11.4804 11.3991 11.513 11.3503 11.535 11.2966C11.5571 11.2429 11.5683 11.1853 11.5678 11.1272C11.5674 11.0691 11.5554 11.0117 11.5326 10.9583C11.5097 10.9049 11.4765 10.8565 11.4347 10.8161L9.11862 8.5Z"
                                    fill="#CC0000"
                                />
                            </svg>
                        </button>
                    )}
                    {isSecondaryFilterSelected && (
                        <button
                            className="saveButton resetButton"
                            onClick={handleResetSecondaryFilters}
                        >
                            {t("secondaryFilters")}{" "}
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M8.5 0.625C4.15738 0.625 0.625 4.15825 0.625 8.5C0.625 12.8418 4.15738 16.375 8.5 16.375C12.8426 16.375 16.375 12.8418 16.375 8.5C16.375 4.15825 12.8426 0.625 8.5 0.625ZM8.5 14.625C5.12337 14.625 2.375 11.8775 2.375 8.5C2.375 5.1225 5.12337 2.375 8.5 2.375C11.8766 2.375 14.625 5.1225 14.625 8.5C14.625 11.8775 11.8766 14.625 8.5 14.625ZM9.11862 8.5L11.4339 6.18475C11.5159 6.10271 11.562 5.99145 11.562 5.87544C11.562 5.75943 11.5159 5.64817 11.4339 5.56613C11.3518 5.48411 11.2406 5.43803 11.1246 5.43803C11.0086 5.43803 10.8973 5.48411 10.8153 5.56613L8.5 7.88138L6.18475 5.56525C6.10224 5.48556 5.99172 5.44146 5.87701 5.44246C5.7623 5.44345 5.65257 5.48946 5.57145 5.57058C5.49034 5.6517 5.44433 5.76143 5.44333 5.87614C5.44233 5.99085 5.48643 6.10136 5.56613 6.18387L7.88138 8.5L5.56613 10.8153C5.48397 10.8973 5.43778 11.0086 5.43769 11.1247C5.43761 11.2408 5.48365 11.3522 5.56569 11.4343C5.64772 11.5165 5.75903 11.5627 5.87513 11.5627C5.99123 11.5628 6.1026 11.5168 6.18475 11.4347L8.5 9.11862L10.8153 11.4339C10.8555 11.4757 10.9038 11.5091 10.9571 11.5321C11.0105 11.5551 11.0679 11.5673 11.1259 11.5678C11.184 11.5684 11.2417 11.5574 11.2954 11.5355C11.3492 11.5136 11.3981 11.4812  11.4393 11.4402C11.4804 11.3991 11.513 11.3503 11.535 11.2966C11.5571 11.2429 11.5683 11.1853 11.5678 11.1272C11.5674 11.0691 11.5554 11.0117 11.5326 10.9583C11.5097 10.9049 11.4765 10.8565 11.4347 10.8161L9.11862 8.5Z"
                                    fill="#CC0000"
                                />
                            </svg>
                        </button>
                    )}
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        marginTop: "10px",
                        justifyContent: "end",
                    }}
                >
                    <button
                        className="saveButton"
                        style={{
                            width: "100%",
                            backgroundColor: isGenerateDashboardDisabled()
                                ? "#e0e0e0"
                                : undefined,
                            color: isGenerateDashboardDisabled()
                                ? "#a0a0a0"
                                : undefined,
                            cursor: isGenerateDashboardDisabled()
                                ? "not-allowed"
                                : undefined,
                        }}
                        onClick={handleGenerateDashboard}
                        disabled={isGenerateDashboardDisabled()}
                    >
                        {t("generateDashboard")}
                    </button>
                </div>
            </div>
            {showDashboard && (
                <div
                    style={{
                        width: "100%",
                        height: "45vh",
                        marginTop: "20px",
                        borderTop: "2px solid #a0a0a0",
                        overflowX: "auto",
                    }}
                >
                    <Bar
                        data={{
                            ...dashboardData,
                            datasets: dashboardData.datasets.map((dataset) => ({
                                ...dataset,
                                label: t(labelKeyMap[dataset.label] || dataset.label), // Translate legend
                                data: dataset.data.map((value) => Math.round(value)),
                            })),
                        }}
                        options={{
                            ...options,
                            maintainAspectRatio: false,
                            plugins: {
                                ...options.plugins,
                                legend: {
                                    ...options.plugins?.legend,
                                    labels: {
                                        ...options.plugins?.legend?.labels,
                                        // This ensures legend labels use the translated label
                                        generateLabels: (chart) => {
                                            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
                                            return original(chart).map((label) => ({
                                                ...label,
                                                text: t(labelKeyMap[label.text] || label.text),
                                            }));
                                        },
                                    },
                                },
                                title: {
                                    ...(props?.options?.plugins?.title || {}),
                                    display: true,
                                    text: t(dashboardKeyMap[props.selectedDashboard] || props.selectedDashboard),
                                    font: { size: 18, weight: "bold" }
                                }
                            },
                            scales: {
                                ...options.scales,
                                y: {
                                    ...options.scales.y,
                                    ticks: {
                                        ...options.scales.y?.ticks,
                                        precision: 0,
                                        stepSize: 1,
                                        callback: function (value, index) {
                                            return this.getLabelForValue(value);
                                        },
                                        font: {
                                            weight: (context) => (context.index === 0 ? "bolder" : "normal"),
                                        },
                                    },
                                },
                            },
                        }}
                    />
                </div>
            )}
            {showDashboard && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "end",
                    }}
                >
                    <button
                        style={{
                            backgroundColor: "#FAF7F5",
                            border: "1px solid #B80211",
                            width: "20%",
                            height: "30px",
                            alignSelf: "flex-end",
                            marginTop: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "5px",
                        }}
                        className="exportButton"
                        onClick={handleExportToExcel}
                    >
                        <img
                            src={require("../../assets/export.png")} // Adjust the filename if different
                            alt="Export Icon"
                            style={{ width: "16px", height: "16px" }} // Adjust size as needed
                        />
                        {t("dashboardData")}
                    </button>
                </div>
            )}
            {!showDashboard && dashboardData?.length === 0 && (
                <div
                    style={{
                        width: "650px",
                        height: "230px",
                        marginLeft: "120px",
                        marginTop: "inherit",
                        border: "2px solid #a0a0a0",
                        padding: "10px 15px",
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                    }}
                >
                    <h3>{t("noDataAvailable")}</h3>
                </div>
            )}
        </>
    );
};

export default StackedBarGraph;
